/**
 * chartViews.ts — Catálogo de vistas de gráfico y paleta compartida.
 *
 * QUÉ HACE: define el identificador, la etiqueta y el icono de cada forma de
 *   visualizar un gráfico (barras verticales, barras apiladas, barras
 *   horizontales, línea, área, matriz, dona, treemap, evolución, tabla, ranking,
 *   velas, burbujas, progreso, semáforo, banda de pronóstico, barras + línea,
 *   radar y gauge) y una paleta de colores reutilizable para las vistas de
 *   porción (dona/treemap).
 *
 * QUÉ NO DEBE HACER: no renderiza UI, no conoce datos ni React.
 *
 * Exporta: ChartViewId, CHART_VIEWS, CHART_VIEW_MENU_ORDER, ChartSlice,
 *   CHART_SLICE_PALETTE, CHART_CHANNEL_COLORS, limitChartSlices,
 *   sortChartSlices, applyThermalSliceColors.
 */
import {
  Activity,
  AreaChart,
  BarChart3,
  BarChartHorizontal,
  CandlestickChart,
  Gauge,
  Grid3x3,
  Layers,
  LayoutGrid,
  LineChart,
  ListChecks,
  ListOrdered,
  Percent,
  PieChart,
  Radar,
  ScatterChart,
  Table,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { resolveThermalColor } from "./chartResponsive";

/** Identificadores de las vistas de gráfico soportadas. */
export type ChartViewId =
  | "bar"
  | "barHorizontal"
  | "line"
  | "area"
  | "matriz"
  | "donut"
  | "treemap"
  | "evolucion"
  | "tabla"
  | "ranking"
  | "velas"
  | "burbujas"
  | "progreso"
  | "semaforo"
  | "banda"
  | "apiladas"
  | "combinada"
  | "radar"
  | "gauge";

/** Opción de vista: id + etiqueta visible + icono. */
interface ChartViewOption {
  id: ChartViewId;
  label: string;
  icon: LucideIcon;
}

/** Catálogo único de vistas (fuente de verdad para etiquetas e iconos). */
export const CHART_VIEWS: Record<ChartViewId, ChartViewOption> = {
  bar: { id: "bar", label: "Barras verticales", icon: BarChart3 },
  barHorizontal: {
    id: "barHorizontal",
    label: "Barras horizontales",
    icon: BarChartHorizontal,
  },
  line: { id: "line", label: "Línea", icon: LineChart },
  area: { id: "area", label: "Área", icon: AreaChart },
  matriz: { id: "matriz", label: "Matriz", icon: Grid3x3 },
  donut: { id: "donut", label: "Dona", icon: PieChart },
  treemap: { id: "treemap", label: "Treemap", icon: LayoutGrid },
  evolucion: { id: "evolucion", label: "Evolución", icon: TrendingUp },
  tabla: { id: "tabla", label: "Tabla", icon: Table },
  ranking: { id: "ranking", label: "Ranking", icon: ListOrdered },
  velas: { id: "velas", label: "Velas japonesas", icon: CandlestickChart },
  burbujas: { id: "burbujas", label: "Burbujas", icon: ScatterChart },
  progreso: { id: "progreso", label: "Barra de progreso", icon: Gauge },
  semaforo: { id: "semaforo", label: "Semáforo", icon: ListChecks },
  banda: {
    id: "banda",
    label: "Banda de pronóstico",
    icon: AreaChart,
  },
  apiladas: { id: "apiladas", label: "Barras apiladas", icon: Layers },
  combinada: {
    id: "combinada",
    label: "Barras + línea",
    icon: Activity,
  },
  radar: { id: "radar", label: "Radar", icon: Radar },
  gauge: { id: "gauge", label: "Gauge", icon: Percent },
};

/**
 * Orden estándar del selector de vistas de Métricas (las 9 vistas base), el
 * mismo en todas las tarjetas: Barras verticales, Barras horizontales, Línea,
 * Velas japonesas, Matriz, Radar, Dona, Treemap y Tabla. `chartViewIdsOf` lo usa
 * para devolver las vistas disponibles ya ordenadas.
 */
export const CHART_VIEW_MENU_ORDER: readonly ChartViewId[] = [
  "bar",
  "barHorizontal",
  "line",
  "velas",
  "matriz",
  "radar",
  "donut",
  "treemap",
  "tabla",
];

/** Porción de un gráfico de participación (dona o treemap). */
export interface ChartSlice {
  name: string;
  value: number;
  color: string;
  /**
   * Punto de datos original de la porción (opcional). Permite que dona y
   * treemap usen el **mismo tooltip glass que las barras** (con el desglose
   * Caja/Web/Total), en vez de un tooltip propio.
   */
  source?: unknown;
}

/** Paleta cíclica para vistas de porción (dona/treemap). */
export const CHART_SLICE_PALETTE = [
  "#2563eb",
  "#a855f7",
  "#10b981",
  "#f59e0b",
  "#06b6d4",
  "#ec4899",
  "#8b5cf6",
  "#84cc16",
  "#ef4444",
  "#14b8a6",
  "#f97316",
  "#6366f1",
];

/** Máximo de porciones visibles en dona/treemap antes de agrupar en "Otros". */
const MAX_CHART_SLICES = 7;

/** Color neutro del segmento agrupado "Otros". */
const OTHERS_SLICE_COLOR = "#94a3b8";

/** Colores de canal compartidos por los gráficos de Métricas. */
export const CHART_CHANNEL_COLORS = {
  caja: "#2563eb",
  tienda: "#a855f7",
  gastos: "#ef4444",
} as const;

/**
 * Limita y ordena las porciones de una vista de participación (dona/treemap) a
 * `max` (7 por defecto). Siempre ordena por valor descendente: la mayor queda
 * como la porción/rectángulo más grande y primera fila de la leyenda, sin
 * agrupar ni perder datos. Si hay más de `max`, deja las primeras `max - 1` y
 * agrupa el resto en `othersLabel` ("Otros", "Otras horas"…) para que la vista
 * no se vuelva interminable (p. ej. 24 horas o muchos métodos de pago).
 */
export function limitChartSlices(
  slices: ChartSlice[],
  max: number = MAX_CHART_SLICES,
  othersLabel: string = "Otros",
): ChartSlice[] {
  const sorted = [...slices].sort((a, b) => b.value - a.value);
  if (sorted.length <= max) return sorted;
  const top = sorted.slice(0, max - 1);
  const restValue = sorted
    .slice(max - 1)
    .reduce((sum, slice) => sum + slice.value, 0);
  return [
    ...top,
    { name: othersLabel, value: restValue, color: OTHERS_SLICE_COLOR },
  ];
}

/**
 * Ordena las porciones por valor descendente (el pico primero). Se usa en dona
 * y treemap para que la porción/rectángulo mayor y la primera fila de la leyenda
 * sean siempre el pico, sin tener que buscarlo con scroll.
 */
export function sortChartSlices(slices: ChartSlice[]): ChartSlice[] {
  return [...slices].sort((a, b) => b.value - a.value);
}

/**
 * Pinta las porciones con la escala térmica compartida: la de mayor valor queda
 * más intensa y la de menor más clara. El tamaño ya indica la proporción; el
 * color refuerza la intensidad sin ruido multicolor.
 */
export function applyThermalSliceColors(slices: ChartSlice[]): ChartSlice[] {
  if (slices.length === 0) return slices;
  const max = slices.reduce((top, slice) => Math.max(top, slice.value), 0);
  return slices.map((slice) => ({
    ...slice,
    color: resolveThermalColor(max > 0 ? slice.value / max : 0),
  }));
}
